import { useMemo, useState } from 'react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { useTranslation } from 'react-i18next';
import {
  useGetChartTradingQuery,
  TimeSeriesPoint,
  JobStatusInfo,
} from '../hooks/queries/use-get-chart-trading';

const METRIC_COLORS = {
  LOW_BASE: '59, 130, 246',
  AVG: '#f4f4f5',
  HIGH_BASE: '34, 197, 94',
} as const;

const METRIC_KEYS = {
  LOW: 'low',
  AVG: 'avg',
  HIGH: 'high',
} as const;

const CHART_METRICS = [
  {
    id: METRIC_KEYS.LOW,
    label: 'Low Price (Buy)',
    color: `rgba(${METRIC_COLORS.LOW_BASE}, 0.6)`,
    selected: true,
  },
  { id: METRIC_KEYS.AVG, label: 'Avg', color: METRIC_COLORS.AVG, selected: true },
  {
    id: METRIC_KEYS.HIGH,
    label: 'High Price (Sell)',
    color: `rgba(${METRIC_COLORS.HIGH_BASE}, 0.6)`,
    selected: true,
  },
] as const;

const getStatusBadgeColor = (status: JobStatusInfo): string => {
  switch (status) {
    case 'ready':
      return 'bg-green-100 text-green-800';
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: JobStatusInfo): string => {
  switch (status) {
    case 'ready':
      return 'ENTSO-E Ready';
    case 'processing':
      return 'ENTSO-E Processing';
    case 'failed':
      return 'ENTSO-E Failed';
    case 'partial':
      return 'ENTSO-E Partial';
    default:
      return 'ENTSO-E Unknown';
  }
};

export const PriceDeltaHeatmap = ({ selectedDate }: { selectedDate: Date }) => {
  const { t } = useTranslation();
  const { data: queryResult, isLoading } = useGetChartTradingQuery({
    date: selectedDate,
    type: 'price-delta-heatmap',
  });

  const chartData = queryResult?.data;
  const status = queryResult?.status;

  const [selectedState, setSelectedState] = useState<Record<string, boolean>>({});

  const fields = useMemo(
    () =>
      CHART_METRICS.map((metric) => ({
        ...metric,
        selected: selectedState[metric.id] ?? true,
      })),
    [selectedState]
  );

  const { data, avgPrice } = useMemo(() => {
    if (!chartData?.energyPrices) {
      return convertToHeatmapData([]);
    }
    return convertToHeatmapData(chartData.energyPrices);
  }, [chartData]);

  const hasNoData = !chartData?.energyPrices || chartData.energyPrices.length === 0;

  const [selected, setSelected] = useState<{
    hour: number | null;
    intervalIdx: number | null;
  }>({ hour: null, intervalIdx: null });
  const onSelect = (hour: number, intervalIdx: number) => setSelected({ hour, intervalIdx });

  return (
    <DashboardCard isLoading={isLoading} className="min-h-[260px]">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-base font-semibold">
          Price Delta Heatmap (vs Daily Avg: €{avgPrice.toFixed(2)})
        </div>
        {status && (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeColor(status)}`}
          >
            {getStatusLabel(status)}
          </span>
        )}
      </div>
      {hasNoData ? (
        <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
          <span className="text-muted-foreground">
            {t('devices.batteryModule.noDataFound')}
          </span>
        </div>
      ) : (
        <>
          <div className="w-full">
            <div className="flex flex-col gap-0.5">
              <div className="mb-1 flex gap-0.5">
                <div className="w-[50px]" />
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} className="min-w-0 flex-1 text-center text-base text-zinc-500">
                    {i.toString().padStart(2, '0')}
                  </div>
                ))}
              </div>
              {['00', '15', '30', '45'].map((min, intervalIdx) => (
                <div key={min} className="flex gap-0.5">
                  <div className="flex w-[50px] items-center justify-end pr-1.5 text-base text-zinc-500">
                    :{min}
                  </div>
                  {data.map((hour) => {
                    const cell = hour.intervals[intervalIdx];
                    const intensity = Math.abs(cell.delta) / 30;
                    const isLow = cell.delta < -10;
                    const isHigh = cell.delta > 10;
                    const category = isLow
                      ? METRIC_KEYS.LOW
                      : isHigh
                        ? METRIC_KEYS.HIGH
                        : METRIC_KEYS.AVG;
                    const categorySelected = fields.find((f) => f.id === category)?.selected;
                    const color = !categorySelected
                      ? '#ffffff'
                      : isLow
                        ? `rgba(${METRIC_COLORS.LOW_BASE}, ${0.3 + intensity * 0.7})`
                        : isHigh
                          ? `rgba(${METRIC_COLORS.HIGH_BASE}, ${0.3 + intensity * 0.7})`
                          : METRIC_COLORS.AVG;
                    const isSelected =
                      selected.hour === hour.hour && selected.intervalIdx === intervalIdx;
                    return (
                      <div
                        key={hour.hour}
                        className={[
                          'h-7 min-w-0 flex-1 rounded',
                          isSelected ? 'ring-2 ring-zinc-900' : '',
                        ].join(' ')}
                        style={{ backgroundColor: color }}
                        onClick={() => onSelect(hour.hour, intervalIdx)}
                        title={`${hour.hour.toString().padStart(2, '0')}:${min} - €${cell.price.toFixed(2)} (${cell.delta > 0 ? '+' : ''}${cell.delta.toFixed(2)})`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="mx-auto mt-3 flex flex-wrap gap-1 md:gap-1">
            {fields.map((f) => (
              <Button
                key={f.id}
                variant="ghost"
                style={{ opacity: !f.selected ? '0.4' : '1' }}
                className="h-min w-min justify-start p-1 px-2"
                onClick={() =>
                  setSelectedState((current) => ({
                    ...current,
                    [f.id]: !f.selected,
                  }))
                }
              >
                <div className="flex items-center gap-2">
                  <span className="size-4 rounded-full" style={{ backgroundColor: f.color }} />
                  <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                    {f.label}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </>
      )}
    </DashboardCard>
  );
};

export default PriceDeltaHeatmap;

type HeatmapInterval = {
  interval: number;
  price: number;
  delta: number;
  opportunity: 'buy' | 'sell' | 'neutral';
};

type HeatmapHour = {
  hour: number;
  intervals: HeatmapInterval[];
};

function convertToHeatmapData(energyPrices: TimeSeriesPoint[]): {
  data: HeatmapHour[];
  avgPrice: number;
} {
  if (!energyPrices || energyPrices.length === 0) {
    return {
      data: Array.from({ length: 24 }, (_, hour) => ({
        hour,
        intervals: Array.from({ length: 4 }, (_, intervalIdx) => ({
          interval: intervalIdx,
          price: 0,
          delta: 0,
          opportunity: 'neutral' as const,
        })),
      })),
      avgPrice: 0,
    };
  }

  const intervalMap = new Map<string, { hour: number; interval: number; price: number }>();

  energyPrices.forEach((point) => {
    const date = new Date(point.time);
    const hour = date.getHours();
    const minutes = date.getMinutes();
    const interval = Math.floor(minutes / 15);
    const key = `${hour}-${interval}`;

    intervalMap.set(key, {
      hour,
      interval,
      price: point.value,
    });
  });

  const allPrices = Array.from(intervalMap.values()).map((item) => item.price);
  const avgPrice =
    allPrices.length > 0 ? allPrices.reduce((sum, p) => sum + p, 0) / allPrices.length : 0;
  const buyThreshold = avgPrice - 10;
  const sellThreshold = avgPrice + 10;

  const data: HeatmapHour[] = Array.from({ length: 24 }, (_, hour) => {
    const intervals: HeatmapInterval[] = Array.from({ length: 4 }, (_, intervalIdx) => {
      const key = `${hour}-${intervalIdx}`;
      const intervalData = intervalMap.get(key);

      if (!intervalData) {
        return {
          interval: intervalIdx,
          price: 0,
          delta: 0,
          opportunity: 'neutral' as const,
        };
      }

      const delta = intervalData.price - avgPrice;
      const opportunity: 'buy' | 'sell' | 'neutral' =
        intervalData.price < buyThreshold
          ? 'buy'
          : intervalData.price > sellThreshold
            ? 'sell'
            : 'neutral';

      return {
        interval: intervalIdx,
        price: intervalData.price,
        delta,
        opportunity,
      };
    });

    return {
      hour,
      intervals,
    };
  });

  return { data, avgPrice };
}
